import React from 'react';
import { PictureOutlined } from '@ant-design/icons';
import { InlineButtonLayout } from '../InlineButtonEditor';
import InlineButtonPreview from './InlineButtonPreview';

const STATIC_VAR_EXAMPLES: Record<string, string> = {
  user_id: '123456789',
  username: '@example_user',
  first_name: 'John',
  last_name: 'Doe',
  bot_name: 'MyBot',
  bot_invite_link: 'https://t.me/MyBot',
};

const ALLOWED_TAGS_RE = /^(b|strong|i|em|u|s|del|code|pre|br)$/i;

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function sanitizeHtml(input: string): string {
  return input.replace(/<\/?([a-zA-Z]+)([^>]*?)>/g, (_match, tag, _attrs) => {
    if (!ALLOWED_TAGS_RE.test(tag)) return '';
    const t = tag.toLowerCase();
    return _match.startsWith('</') ? `</${t}>` : `<${t}>`;
  });
}

function resolveVarsAsHtml(text: string, dynamicVarFields: string[]): string {
  // First sanitize the raw template text, then inject variable spans
  const sanitizedText = sanitizeHtml(text);
  return sanitizedText.replace(/\{([^}]+)\}/g, (_match, varName) => {
    const safeVarName = escapeHtml(varName);
    if (varName in STATIC_VAR_EXAMPLES) {
      const safeExample = escapeHtml(STATIC_VAR_EXAMPLES[varName]);
      return `<span class="tg-var-static" title="变量: ${safeVarName}">${safeExample}</span>`;
    }
    if (dynamicVarFields.includes(varName)) {
      return `<span class="tg-var-dynamic" title="动态变量: ${safeVarName}">{${safeVarName}}</span>`;
    }
    return `<span class="tg-var-unknown">{${safeVarName}}</span>`;
  });
}

function renderTextNodes(text: string, dynamicVarFields: string[]): React.ReactNode[] {
  const parts = text.split(/(\{[^}]+\})/g);
  return parts.map((part, i) => {
    const varMatch = part.match(/^\{(.+)\}$/);
    if (!varMatch) return <React.Fragment key={i}>{part}</React.Fragment>;
    const varName = varMatch[1];
    if (varName in STATIC_VAR_EXAMPLES) {
      return (
        <span key={i} className="tg-var-static" title={`变量: ${varName}`}>
          {STATIC_VAR_EXAMPLES[varName]}
        </span>
      );
    }
    if (dynamicVarFields.includes(varName)) {
      return (
        <span key={i} className="tg-var-dynamic" title={`动态变量: ${varName}`}>
          {`{${varName}}`}
        </span>
      );
    }
    return (
      <span key={i} className="tg-var-unknown">{part}</span>
    );
  });
}

export interface ChatBubbleProps {
  sender: 'user' | 'bot';
  senderName?: string;
  text?: string;
  displayText?: string;
  parseMode?: 'HTML' | 'Markdown';
  mediaUrl?: string | null;
  mediaType?: 'photo' | 'video';
  buttons?: InlineButtonLayout;
  displayButtons?: InlineButtonLayout;
  dynamicVarFields?: string[];
  onNavigate?: (targetPageIndex: number) => void;
  onSwitchContent?: (altText: string, altButtons?: InlineButtonLayout) => void;
}

const ChatBubble: React.FC<ChatBubbleProps> = ({
  sender,
  senderName,
  text = '',
  displayText,
  parseMode = 'HTML',
  mediaUrl,
  mediaType,
  buttons,
  displayButtons,
  dynamicVarFields = [],
  onNavigate,
  onSwitchContent,
}) => {
  const isBot = sender === 'bot';
  const isPhoto = mediaType === 'photo';
  const isVideo = mediaType === 'video';
  const hasMedia = isPhoto || isVideo;

  const activeText = displayText !== undefined ? displayText : text;

  const renderText = () => {
    if (!activeText) return null;

    if (parseMode === 'HTML') {
      const html = resolveVarsAsHtml(activeText, dynamicVarFields);
      return (
        <span
          className="tg-bubble-text"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    }

    // Markdown: show approximate text with variable highlights + notice
    return (
      <>
        <span className="tg-bubble-text">
          {renderTextNodes(activeText, dynamicVarFields)}
        </span>
        <span className="tg-markdown-note">Markdown 预览为近似效果</span>
      </>
    );
  };

  const renderMedia = () => {
    if (!hasMedia) return null;
    if (isVideo) {
      return (
        <div className="tg-bubble-image-wrapper">
          <div className="tg-bubble-image-placeholder">
            <PictureOutlined style={{ fontSize: 24 }} />
            <span>视频</span>
            {mediaUrl && (
              <span style={{ fontSize: 10, wordBreak: 'break-all', maxWidth: 160 }}>
                {mediaUrl.slice(0, 40)}{mediaUrl.length > 40 ? '...' : ''}
              </span>
            )}
          </div>
        </div>
      );
    }
    if (!mediaUrl) {
      return (
        <div className="tg-bubble-image-wrapper">
          <div className="tg-bubble-image-placeholder">
            <PictureOutlined style={{ fontSize: 24 }} />
            <span>图片（未填写URL）</span>
          </div>
        </div>
      );
    }
    return (
      <div className="tg-bubble-image-wrapper">
        <img
          src={mediaUrl}
          alt="预览图片"
          className="tg-bubble-image"
          onError={(e) => {
            const el = e.currentTarget;
            el.style.display = 'none';
            const placeholder = el.nextElementSibling as HTMLElement;
            if (placeholder) placeholder.style.display = 'flex';
          }}
        />
        <div className="tg-bubble-image-placeholder" style={{ display: 'none' }}>
          <PictureOutlined style={{ fontSize: 24 }} />
          <span>图片无法加载</span>
        </div>
      </div>
    );
  };

  const now = new Date();
  const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

  return (
    <div className={`tg-msg-wrapper ${sender}`}>
      {isBot && senderName && (
        <span className="tg-sender-name">{senderName}</span>
      )}
      <div className={`tg-bubble ${sender}`}>
        {renderMedia()}
        {renderText()}
        <span className="tg-bubble-time">{timeStr}</span>
      </div>
      {(displayButtons || buttons) && ((displayButtons || buttons)!.length > 0) && (
        <InlineButtonPreview
          buttons={(displayButtons || buttons)!}
          onNavigate={onNavigate}
          onSwitchContent={onSwitchContent}
        />
      )}
    </div>
  );
};

export default ChatBubble;
